"use client"

import type { VRAudioSettings } from "./vr-store"
import { createFOADecoder } from "./vr-ambisonics"
import type { FOADecoder } from "./vr-ambisonics"

interface VRAudioGraph {
  context: AudioContext
  source: MediaElementAudioSourceNode
  inputGain: GainNode
  lowFilter: BiquadFilterNode
  midFilter: BiquadFilterNode
  highFilter: BiquadFilterNode
  sameLeft: GainNode
  crossLeft: GainNode
  sameRight: GainNode
  crossRight: GainNode
  balance: StereoPannerNode
  roomDry: GainNode
  roomWet: GainNode
  convolver: ConvolverNode
  limiter: DynamicsCompressorNode
  panner: PannerNode
  originalOut: GainNode
  enhancedOut: GainNode
  spatialOut: GainNode
  upmixLeft: PannerNode
  upmixRight: PannerNode
  upmixRear: PannerNode
  upmixRearGain: GainNode
  upmixOut: GainNode
  foaDecoder: FOADecoder
  ambisonicOut: GainNode
  roomSize: number
}

const graphs = new WeakMap<HTMLMediaElement, VRAudioGraph>()
let activeMedia: HTMLMediaElement | null = null

function canProcessMedia(media: HTMLMediaElement) {
  const sourceUrl = media.currentSrc || media.src
  if (!sourceUrl) return false
  try {
    const url = new URL(sourceUrl, window.location.href)
    return url.origin === window.location.origin || url.protocol === "blob:" || url.protocol === "data:" || media.crossOrigin !== null
  } catch {
    return false
  }
}

function setParam(param: AudioParam, value: number, context: AudioContext) {
  param.cancelScheduledValues(context.currentTime)
  param.setTargetAtTime(value, context.currentTime, 0.015)
}

function makeRoomImpulse(context: AudioContext, size: number) {
  const duration = 0.12 + size * 1.8
  const length = Math.max(1, Math.floor(context.sampleRate * duration))
  const buffer = context.createBuffer(2, length, context.sampleRate)

  for (let channel = 0; channel < buffer.numberOfChannels; channel += 1) {
    const data = buffer.getChannelData(channel)
    for (let index = 0; index < length; index += 1) {
      const progress = index / length
      data[index] = (Math.random() * 2 - 1) * Math.pow(1 - progress, 2 + (1 - size) * 5)
    }
  }

  return buffer
}

function configureLimiter(limiter: DynamicsCompressorNode) {
  limiter.threshold.value = -1
  limiter.knee.value = 0
  limiter.ratio.value = 20
  limiter.attack.value = 0.003
  limiter.release.value = 0.12
}

function configureSpatialPanner(panner: PannerNode) {
  panner.panningModel = "HRTF"
  panner.distanceModel = "inverse"
  panner.refDistance = 1
  panner.maxDistance = 100
  panner.coneInnerAngle = 360
  panner.coneOuterAngle = 360
}

function setPannerDirection(panner: PannerNode, azimuthDegrees: number, elevationDegrees = 0, distance = 1) {
  const azimuth = azimuthDegrees * Math.PI / 180
  const elevation = elevationDegrees * Math.PI / 180
  const horizontalDistance = Math.cos(elevation) * distance
  panner.positionX.value = Math.sin(azimuth) * horizontalDistance
  panner.positionY.value = Math.sin(elevation) * distance
  panner.positionZ.value = -Math.cos(azimuth) * horizontalDistance
}

function createGraph(media: HTMLMediaElement): VRAudioGraph | null {
  if (!canProcessMedia(media)) return null
  let context: AudioContext | null = null
  let source: MediaElementAudioSourceNode | null = null
  try {
    context = new AudioContext({ latencyHint: "interactive" })
    source = context.createMediaElementSource(media)
    const inputGain = context.createGain()
    const lowFilter = context.createBiquadFilter()
    const midFilter = context.createBiquadFilter()
    const highFilter = context.createBiquadFilter()
    const splitter = context.createChannelSplitter(2)
    const merger = context.createChannelMerger(2)
    const sameLeft = context.createGain()
    const crossLeft = context.createGain()
    const sameRight = context.createGain()
    const crossRight = context.createGain()
    const balance = context.createStereoPanner()
    const roomDry = context.createGain()
    const roomWet = context.createGain()
    const convolver = context.createConvolver()
    const limiter = context.createDynamicsCompressor()
    const panner = context.createPanner()
    const upmixSplitter = context.createChannelSplitter(2)
    const upmixLeft = context.createPanner()
    const upmixRight = context.createPanner()
    const upmixRear = context.createPanner()
    const upmixRearGain = context.createGain()
    const upmixOut = context.createGain()
    const foaDecoder = createFOADecoder(context)
    const ambisonicLimiter = context.createDynamicsCompressor()
    const ambisonicOut = context.createGain()
    const originalOut = context.createGain()
    const enhancedOut = context.createGain()
    const spatialOut = context.createGain()

    originalOut.gain.value = 1
    enhancedOut.gain.value = 0
    spatialOut.gain.value = 0
    upmixOut.gain.value = 0
    ambisonicOut.gain.value = 0
    upmixRearGain.gain.value = 0
    roomDry.gain.value = 1
    roomWet.gain.value = 0
    sameLeft.gain.value = 1
    sameRight.gain.value = 1
    crossLeft.gain.value = 0
    crossRight.gain.value = 0

    lowFilter.type = "lowshelf"
    lowFilter.frequency.value = 180
    midFilter.type = "peaking"
    midFilter.frequency.value = 1200
    midFilter.Q.value = 0.75
    highFilter.type = "highshelf"
    highFilter.frequency.value = 4500

    configureLimiter(limiter)
    configureLimiter(ambisonicLimiter)
    configureSpatialPanner(panner)
    configureSpatialPanner(upmixLeft)
    configureSpatialPanner(upmixRight)
    configureSpatialPanner(upmixRear)
    upmixLeft.rolloffFactor = 0
    upmixRight.rolloffFactor = 0
    upmixRear.rolloffFactor = 0
    setPannerDirection(upmixRear, 180)

    source.connect(originalOut).connect(context.destination)
    source.connect(inputGain)
    inputGain.connect(lowFilter).connect(midFilter).connect(highFilter)
    highFilter.connect(splitter)
    highFilter.connect(foaDecoder.input)

    splitter.connect(sameLeft, 0)
    splitter.connect(crossLeft, 0)
    splitter.connect(sameRight, 1)
    splitter.connect(crossRight, 1)
    sameLeft.connect(merger, 0, 0)
    crossRight.connect(merger, 0, 0)
    sameRight.connect(merger, 0, 1)
    crossLeft.connect(merger, 0, 1)

    merger.connect(balance)
    balance.connect(roomDry).connect(limiter)
    balance.connect(convolver).connect(roomWet).connect(limiter)
    limiter.connect(enhancedOut).connect(context.destination)
    limiter.connect(panner).connect(spatialOut).connect(context.destination)
    balance.connect(upmixSplitter)
    upmixSplitter.connect(upmixLeft, 0)
    upmixSplitter.connect(upmixRight, 1)
    upmixLeft.connect(upmixOut)
    upmixRight.connect(upmixOut)
    convolver.connect(upmixRearGain).connect(upmixRear).connect(upmixOut)
    upmixOut.connect(context.destination)
    foaDecoder.output.connect(ambisonicLimiter).connect(ambisonicOut).connect(context.destination)

    const graph: VRAudioGraph = {
      context,
      source,
      inputGain,
      lowFilter,
      midFilter,
      highFilter,
      sameLeft,
      crossLeft,
      sameRight,
      crossRight,
      balance,
      roomDry,
      roomWet,
      convolver,
      limiter,
      panner,
      originalOut,
      enhancedOut,
      spatialOut,
      upmixLeft,
      upmixRight,
      upmixRear,
      upmixRearGain,
      upmixOut,
      foaDecoder,
      ambisonicOut,
      roomSize: -1,
    }
    graphs.set(media, graph)
    return graph
  } catch {
    try {
      if (context && source) source.connect(context.destination)
      context?.resume().catch(() => {})
    } catch {}
    return null
  }
}

function updateGraph(graph: VRAudioGraph, settings: VRAudioSettings) {
  const { context } = graph
  setParam(graph.inputGain.gain, Math.pow(10, settings.boostDb / 20), context)
  setParam(graph.lowFilter.gain, settings.bass, context)
  setParam(graph.midFilter.gain, settings.mid, context)
  setParam(graph.highFilter.gain, settings.treble, context)
  setParam(graph.balance.pan, settings.balance, context)

  const widthSame = (1 + settings.stereoWidth) / 2
  const widthCross = (1 - settings.stereoWidth) / 2
  const same = (1 - settings.crossfeed) * widthSame + settings.crossfeed * widthCross
  const cross = (1 - settings.crossfeed) * widthCross + settings.crossfeed * widthSame
  setParam(graph.sameLeft.gain, same, context)
  setParam(graph.sameRight.gain, same, context)
  setParam(graph.crossLeft.gain, cross, context)
  setParam(graph.crossRight.gain, cross, context)

  setParam(graph.roomDry.gain, Math.cos(settings.roomMix * Math.PI * 0.5), context)
  setParam(graph.roomWet.gain, Math.sin(settings.roomMix * Math.PI * 0.5), context)
  if ((settings.roomMix > 0 || (settings.mode === "upmix" && settings.upmixRear > 0)) && Math.abs(graph.roomSize - settings.roomSize) >= 0.02) {
    graph.roomSize = settings.roomSize
    graph.convolver.buffer = makeRoomImpulse(context, settings.roomSize)
  }

  const isSpatial = settings.mode === "front" || settings.mode === "positioned"
  const spatialMix = isSpatial ? settings.spatialMix : 0
  setParam(graph.originalOut.gain, settings.mode === "original" ? 1 : 0, context)
  setParam(graph.enhancedOut.gain, settings.mode === "enhanced" ? 1 : isSpatial ? Math.cos(spatialMix * Math.PI * 0.5) : 0, context)
  setParam(graph.spatialOut.gain, isSpatial ? Math.sin(spatialMix * Math.PI * 0.5) : 0, context)
  setParam(graph.upmixOut.gain, settings.mode === "upmix" ? 0.68 : 0, context)
  setParam(graph.ambisonicOut.gain, settings.mode === "ambisonic" ? 1 : 0, context)
  setParam(graph.upmixRearGain.gain, settings.mode === "upmix" ? settings.upmixRear : 0, context)

  setPannerDirection(graph.upmixLeft, -settings.upmixSpread)
  setPannerDirection(graph.upmixRight, settings.upmixSpread)
  graph.foaDecoder.setChannelOrder(settings.ambisonicOrder)

  const azimuth = (settings.mode === "front" ? 0 : settings.azimuth) * Math.PI / 180
  const elevation = (settings.mode === "front" ? 0 : settings.elevation) * Math.PI / 180
  const horizontalDistance = Math.cos(elevation) * settings.distance
  graph.panner.positionX.value = Math.sin(azimuth) * horizontalDistance
  graph.panner.positionY.value = Math.sin(elevation) * settings.distance
  graph.panner.positionZ.value = -Math.cos(azimuth) * horizontalDistance
  graph.panner.rolloffFactor = settings.rolloff
}

export function activateVRAudio(media: HTMLMediaElement | null, settings: VRAudioSettings) {
  if (!media) return false
  const graph = graphs.get(media) ?? createGraph(media)
  if (!graph) return false
  updateGraph(graph, settings)
  activeMedia = media
  graph.context.resume().catch(() => {})
  return true
}

function fillTone(data: Float32Array, start: number, length: number, sampleRate: number, gain: number) {
  for (let offset = 0; offset < length; offset += 1) {
    const envelope = Math.sin(Math.PI * offset / length) ** 2
    data[start + offset] += Math.sin(2 * Math.PI * 440 * offset / sampleRate) * envelope * gain
  }
}

export function playVRAudioCalibration(settings: VRAudioSettings) {
  if (!activeMedia) return false
  const graph = graphs.get(activeMedia)
  if (!graph) return false
  const { context } = graph
  const directions = [
    { azimuth: 0, elevation: 0 },
    { azimuth: 90, elevation: 0 },
    { azimuth: 180, elevation: 0 },
    { azimuth: -90, elevation: 0 },
    { azimuth: 0, elevation: 70 },
  ]
  const toneSeconds = 0.32
  const stepSeconds = 0.5

  context.resume().catch(() => {})

  if (settings.mode === "ambisonic") {
    const totalLength = Math.ceil(context.sampleRate * stepSeconds * directions.length)
    const buffer = context.createBuffer(4, totalLength, context.sampleRate)
    const channelForComponent = settings.ambisonicOrder === "ambix" ? [0, 3, 1, 2] : [0, 1, 2, 3]

    directions.forEach((direction, index) => {
      const azimuth = direction.azimuth * Math.PI / 180
      const elevation = direction.elevation * Math.PI / 180
      const horizontal = Math.cos(elevation)
      const start = Math.floor(index * stepSeconds * context.sampleRate)
      const length = Math.floor(toneSeconds * context.sampleRate)
      const components = [
        settings.ambisonicOrder === "fuma" ? Math.SQRT1_2 : 1,
        Math.cos(azimuth) * horizontal,
        -Math.sin(azimuth) * horizontal,
        Math.sin(elevation),
      ]
      components.forEach((gain, component) => {
        fillTone(buffer.getChannelData(channelForComponent[component]), start, length, context.sampleRate, gain * 0.18)
      })
    })

    const source = context.createBufferSource()
    source.buffer = buffer
    source.connect(graph.foaDecoder.input)
    source.start()
    return true
  }

  directions.forEach((direction, index) => {
    const length = Math.floor(toneSeconds * context.sampleRate)
    const buffer = context.createBuffer(1, length, context.sampleRate)
    fillTone(buffer.getChannelData(0), 0, length, context.sampleRate, 0.16)
    const source = context.createBufferSource()
    const panner = context.createPanner()
    source.buffer = buffer
    configureSpatialPanner(panner)
    panner.rolloffFactor = 0
    setPannerDirection(panner, direction.azimuth, direction.elevation)
    source.connect(panner).connect(context.destination)
    source.start(context.currentTime + index * stepSeconds)
  })
  return true
}

export function updateVRAudio(media: HTMLMediaElement | null, settings: VRAudioSettings) {
  if (!media) return
  const graph = graphs.get(media)
  if (graph) updateGraph(graph, settings)
}

export function bypassVRAudio(media: HTMLMediaElement | null) {
  if (!media) return
  const graph = graphs.get(media)
  if (!graph) return
  setParam(graph.originalOut.gain, 1, graph.context)
  setParam(graph.enhancedOut.gain, 0, graph.context)
  setParam(graph.spatialOut.gain, 0, graph.context)
  setParam(graph.upmixOut.gain, 0, graph.context)
  setParam(graph.ambisonicOut.gain, 0, graph.context)
}

export function updateVRAudioListener(
  media: HTMLMediaElement | null,
  forward: readonly [number, number, number],
  up: readonly [number, number, number],
  headTracking: boolean,
) {
  if (!media) return
  const graph = graphs.get(media)
  if (!graph) return
  const listener = graph.context.listener
  const nextForward = headTracking ? forward : [0, 0, -1]
  const nextUp = headTracking ? up : [0, 1, 0]
  const now = graph.context.currentTime

  listener.forwardX.setValueAtTime(nextForward[0], now)
  listener.forwardY.setValueAtTime(nextForward[1], now)
  listener.forwardZ.setValueAtTime(nextForward[2], now)
  listener.upX.setValueAtTime(nextUp[0], now)
  listener.upY.setValueAtTime(nextUp[1], now)
  listener.upZ.setValueAtTime(nextUp[2], now)
}
